import React from 'react';
import {
  WxbButton,
  WxbInput,
  WxbInputNumber,
  WxbSelect,
} from '../wxb-ui';
import {
  conditionNeedsValue,
  GUARD_OPERATOR_LABELS,
} from './equipmentStateCatalogModel';
import { formatAttributeOptionLabel } from './attributeCodeGeneration';
import type {
  EquipmentStateAttribute,
  EquipmentStateCondition,
  EquipmentStateGuardOperator,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  attributes: EquipmentStateAttribute[];
  conditions: EquipmentStateCondition[];
  disabled?: boolean;
  emptyText: string;
  onChange: (conditions: EquipmentStateCondition[]) => void;
}

const OPERATORS_BY_KIND: Record<EquipmentStateAttribute['kind'], EquipmentStateGuardOperator[]> = {
  DISCRETE: ['EQ', 'NE', 'IN', 'NOT_IN'],
  COUNTER: ['EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE'],
  CLOCK: ['IS_SET', 'IS_NOT_SET', 'NOT_EXPIRED', 'EXPIRED'],
};

const TypedConditionEditor: React.FC<Props> = ({
  attributes,
  conditions,
  disabled = false,
  emptyText,
  onChange,
}) => {
  const update = (index: number, patch: Partial<EquipmentStateCondition>) => {
    onChange(conditions.map((condition, itemIndex) => (
      itemIndex === index ? { ...condition, ...patch } : condition
    )));
  };

  const remove = (index: number) => onChange(conditions.filter((_, itemIndex) => itemIndex !== index));

  const add = () => {
    const attribute = attributes[0];
    const operator = attribute ? OPERATORS_BY_KIND[attribute.kind][0] : 'EQ';
    onChange([
      ...conditions,
      {
        attributeCode: attribute?.code ?? '',
        operator,
        value: attribute?.kind === 'DISCRETE' ? attribute.values[0]?.code ?? ''
          : attribute?.kind === 'COUNTER' ? 0
            : undefined,
      },
    ]);
  };

  return (
    <div className="esc-condition-editor">
      {conditions.length === 0 && <div className="esc-inline-empty">{emptyText}</div>}
      {conditions.map((condition, index) => {
        const attribute = attributes.find((item) => item.code === condition.attributeCode);
        const operator = condition.operator;
        const needsValue = conditionNeedsValue(operator);
        const isList = operator === 'IN' || operator === 'NOT_IN';
        const allowedOperators: EquipmentStateGuardOperator[] = attribute
          ? OPERATORS_BY_KIND[attribute.kind]
          : ['EQ'];
        const discreteOptions = attribute?.kind === 'DISCRETE'
          ? attribute.values.map((value) => ({ value: value.code, label: `${value.name} (${value.code})` }))
          : [];
        return (
          <div className="esc-condition-row" key={`${condition.attributeCode}-${index}`}>
            <WxbSelect
              aria-label={`条件 ${index + 1} 属性`}
              value={condition.attributeCode || undefined}
              disabled={disabled}
              options={attributes.map((item) => ({
                value: item.code,
                label: formatAttributeOptionLabel(item.name, item.code),
              }))}
              placeholder="选择属性"
              onChange={(attributeCode) => {
                const nextAttribute = attributes.find((item) => item.code === String(attributeCode));
                const nextOperator = nextAttribute ? OPERATORS_BY_KIND[nextAttribute.kind][0] : 'EQ';
                update(index, {
                  attributeCode: String(attributeCode),
                  operator: nextOperator,
                  value: nextAttribute?.kind === 'DISCRETE' ? nextAttribute.values[0]?.code ?? ''
                    : nextAttribute?.kind === 'COUNTER' ? 0
                      : undefined,
                });
              }}
            />
            <WxbSelect
              aria-label={`条件 ${index + 1} 运算符`}
              value={operator}
              disabled={disabled}
              options={allowedOperators.map((value) => ({ value, label: GUARD_OPERATOR_LABELS[value] }))}
              onChange={(value) => {
                const nextOperator = value as EquipmentStateGuardOperator;
                update(index, {
                  operator: nextOperator,
                  ...(conditionNeedsValue(nextOperator) ? {} : { value: undefined }),
                });
              }}
            />
            <div className="esc-condition-value">
              {!needsValue ? (
                <span className="esc-condition-no-value">无需比较值</span>
              ) : isList && discreteOptions.length > 0 ? (
                <WxbSelect
                  aria-label={`条件 ${index + 1} 比较值`}
                  mode="multiple"
                  value={Array.isArray(condition.value) ? condition.value.map(String) : []}
                  disabled={disabled}
                  options={discreteOptions}
                  placeholder="选择一个或多个状态值"
                  onChange={(value) => update(index, { value: (value as string[]).map(String) })}
                />
              ) : discreteOptions.length > 0 && ['EQ', 'NE'].includes(operator) ? (
                <WxbSelect
                  aria-label={`条件 ${index + 1} 比较值`}
                  value={typeof condition.value === 'string' ? condition.value : undefined}
                  disabled={disabled}
                  options={discreteOptions}
                  placeholder="选择状态值"
                  onChange={(value) => update(index, { value: String(value) })}
                />
              ) : attribute?.kind === 'COUNTER' ? (
                <WxbInputNumber
                  aria-label={`条件 ${index + 1} 比较值`}
                  value={typeof condition.value === 'number' ? condition.value : null}
                  disabled={disabled}
                  placeholder="数值"
                  onChange={(value) => update(index, { value: typeof value === 'number' ? value : null })}
                />
              ) : (
                <WxbInput
                  aria-label={`条件 ${index + 1} 比较值`}
                  value={typeof condition.value === 'string' || typeof condition.value === 'number'
                    ? String(condition.value)
                    : ''}
                  disabled={disabled}
                  placeholder="比较值"
                  onChange={(event) => update(index, { value: event.target.value })}
                />
              )}
            </div>
            <WxbButton
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => remove(index)}
              aria-label={`删除条件 ${index + 1}`}
            >
              删除
            </WxbButton>
          </div>
        );
      })}
      <WxbButton
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled || attributes.length === 0}
        onClick={add}
      >
        添加条件
      </WxbButton>
    </div>
  );
};

export default TypedConditionEditor;
